import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorItemStretchModal} from '@Modals/poster-editor-item-stretch-modal';
import {openPosterEditorItemFlipModal} from '@Modals/poster-editor-item-flip-modal';
import {openPosterEditorShadowModal} from '@Modals/poster-editor-item-shadow-modal';
import {openPosterEditorVectorFillModal} from '@Modals/poster-editor-vector-fill-modal';
import {useEditorPrefetchVectorMobileModules} from '@Hooks/poster-editor/useEditorPrefetchVectorMobileModules';
import {ElementItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {VectorItemColorFill} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/vector-item-color-fill/vector-item-color-fill';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './vector-bottom-bar.module.scss';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';
import {openPosterEditorVectorBorderModal} from '@Modals/poster-editor-vector-border-modal/poster-editor-vector-border-modal-library';

export function VectorBottomBar(): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const isLocked = useGetKeyFromActiveItem('lockMovementX');
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  useEditorPrefetchVectorMobileModules();

  if (!activeItem?.isVector()) {
    return null;
  }

  const getCustomFillContent = (): ReactElement<any> => {
    return <VectorItemColorFill />;
  };

  const pulsatingDotForCopyStyles = (): ReactElement<any> | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  const onClickHandler = (itemId: ElementItemBottomBarOptions): void => {
    switch (itemId) {
      case ElementItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case ElementItemBottomBarOptions.FIll:
        openPosterEditorVectorFillModal();
        break;
      case ElementItemBottomBarOptions.OUTLINE:
        openPosterEditorVectorBorderModal();
        break;
      case ElementItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case ElementItemBottomBarOptions.SHADOW:
        openPosterEditorShadowModal();
        break;
      case ElementItemBottomBarOptions.FLIP:
        openPosterEditorItemFlipModal();
        break;
      case ElementItemBottomBarOptions.STRETCH:
        openPosterEditorItemStretchModal();
        break;
      case ElementItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case ElementItemBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case ElementItemBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case ElementItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case ElementItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case ElementItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem?.uid);
        break;
      default:
    }
  };

  return (
    <BottomBar
      id="poster-editing-default-bottom-bar"
      onClick={onClickHandler}
      items={[
        {
          id: ElementItemBottomBarOptions.FIll,
          customContent: getCustomFillContent(),
          text: window.i18next.t('pmwjs_fill'),
          isHidden: !activeItem.isCustomisable,
        },
        {
          id: ElementItemBottomBarOptions.OUTLINE,
          icon: 'icon-outline',
          text: window.i18next.t('pmwjs_outline'),
          isHidden: !activeItem.isCustomisable,
        },
        {
          id: ElementItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: ElementItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: ElementItemBottomBarOptions.SHADOW,
          icon: 'icon-shadow',
          text: window.i18next.t('pmwjs_shadow'),
        },
        {
          id: ElementItemBottomBarOptions.FLIP,
          icon: 'icon-flip-horizontal',
          text: window.i18next.t('pmwjs_flip'),
        },
        {
          id: ElementItemBottomBarOptions.STRETCH,
          icon: 'icon-fit-to-width',
          text: window.i18next.t('pmwjs_stretch'),
        },
        {
          id: ElementItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: ElementItemBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: ElementItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: ElementItemBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: ElementItemBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: ElementItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
    />
  );
}
